import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useSyncExternalStore } from 'react'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import {
  DxTradeOrder,
  DxTradeOrderStatuses,
  DxTradeOrderType,
  DxTradeOrderTypes,
} from '@model/DxTrade.types'

import { DXTRADE_POSITION_EFFECT } from '../../utils/constants'
import { filterBySideAndDateRange } from '../utils/filters'
import { getPortfolioStoreSnapshot, subscribeToPortfolioStore } from './useDxtradeAccountWebSocket'

export type PendingOrderWithTPSL = DxTradeOrder & {
  stopLoss?: number | null
  takeProfit?: number | null
  stopLossOrderCode?: string | null
  takeProfitOrderCode?: string | null
  stopLossClientOrderId?: string | null
  takeProfitClientOrderId?: string | null
}

function findPendingOrderStopLossTakeProfit(
  pendingOrder: DxTradeOrder,
  workingOrders: DxTradeOrder[]
): {
  stopLoss?: number | null
  takeProfit?: number | null
  stopLossOrderCode?: string | null
  takeProfitOrderCode?: string | null
  stopLossClientOrderId?: string | null
  takeProfitClientOrderId?: string | null
} {
  const pendingOrderIdStr = pendingOrder.orderId.toString()
  const relatedOrders = workingOrders.filter(
    (order) =>
      order.orderId.toString() !== pendingOrderIdStr &&
      order.legs?.some((l) => l.positionCode === pendingOrderIdStr)
  )

  let stopLoss: number | null = null
  let takeProfit: number | null = null
  let stopLossOrderCode: string | null = null
  let takeProfitOrderCode: string | null = null
  let stopLossClientOrderId: string | null = null
  let takeProfitClientOrderId: string | null = null

  for (const order of relatedOrders) {
    for (const leg of order.legs || []) {
      const price = leg.price
      if (price == null) continue
      if (order.type === DxTradeOrderTypes.STOP && stopLoss === null) {
        stopLoss = price
        stopLossOrderCode = order.orderCode ?? null
        stopLossClientOrderId = order.clientOrderId ?? null
      }
      if (order.type === DxTradeOrderTypes.LIMIT && takeProfit === null) {
        takeProfit = price
        takeProfitOrderCode = order.orderCode ?? null
        takeProfitClientOrderId = order.clientOrderId ?? null
      }
    }
  }

  return {
    stopLoss,
    takeProfit,
    stopLossOrderCode,
    takeProfitOrderCode,
    stopLossClientOrderId,
    takeProfitClientOrderId,
  }
}

type PendingOrdersFilters = {
  side?: 'BUY' | 'SELL'
  ofType?: DxTradeOrderType
  issuedFrom?: string
  issuedTo?: string
}

export const useDxtradePendingOrders = (
  accountCode: string,
  filters?: PendingOrdersFilters
): {
  data: PendingOrderWithTPSL[] | undefined
  isLoading: boolean
  forceRefresh: (skipLoadingFlag?: boolean) => void
} => {
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()

  const subscribe = useCallback(
    (listener: () => void) => subscribeToPortfolioStore(accountCode, listener),
    [accountCode]
  )
  const wsPortfolio = useSyncExternalStore(subscribe, () => getPortfolioStoreSnapshot(accountCode))

  const { data: restData, isLoading: restIsLoading } = useQuery({
    queryKey: queryKeys.trade.dxtrade.pendingOrders(accountCode),
    queryFn: () => apiClient.getDxtradeOrders(accountCode),
    enabled: !!accountCode,
  })

  const data = useMemo<PendingOrderWithTPSL[] | undefined>(() => {
    const sourceOrders = wsPortfolio ? wsPortfolio.orders : restData?.orders
    if (!sourceOrders) return undefined

    const workingOrders = sourceOrders.filter(
      (order) => order.status === DxTradeOrderStatuses.WORKING
    )

    const pendingOrders = workingOrders.filter((order) =>
      (order.legs ?? []).some((l) => l.positionEffect === DXTRADE_POSITION_EFFECT.OPEN)
    )

    const filtered = filterBySideAndDateRange(
      pendingOrders,
      filters
        ? {
            side: filters.side,
            from: filters.issuedFrom,
            to: filters.issuedTo,
          }
        : undefined,
      (order) => order.side ?? undefined,
      (order) => order.issueTime
    ).filter((order) => !filters?.ofType || order.type === filters.ofType)

    return filtered.map((pendingOrder) => {
      const {
        stopLoss,
        takeProfit,
        stopLossOrderCode,
        takeProfitOrderCode,
        stopLossClientOrderId,
        takeProfitClientOrderId,
      } = findPendingOrderStopLossTakeProfit(pendingOrder, workingOrders)

      return {
        ...pendingOrder,
        stopLoss,
        takeProfit,
        stopLossOrderCode,
        takeProfitOrderCode,
        stopLossClientOrderId,
        takeProfitClientOrderId,
      }
    })
  }, [
    wsPortfolio,
    restData,
    filters?.side,
    filters?.ofType,
    filters?.issuedFrom,
    filters?.issuedTo,
  ])

  const isLoading = wsPortfolio ? false : restIsLoading

  const forceRefresh = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.trade.dxtrade.pendingOrders(accountCode),
    })
  }, [queryClient, accountCode])

  return {
    data,
    isLoading,
    forceRefresh,
  }
}
